import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import { alpha, keyframes, lighten } from '@mui/material/styles';
import type React from 'react';

interface AkomiDialogActionsProps {
  /** Pre-defined action button layout */
  variant: 'confirm' | 'cancel-confirm' | 'delete-cancel' | 'save-cancel' | 'close';
  /** Handler for the primary action (Confirm, Save, Delete, Close) */
  onPrimary?: () => void;
  /** Handler for the secondary action (Cancel, No) */
  onSecondary?: () => void;
  /** Override label for the primary button */
  primaryLabel?: string;
  /** Override label for the secondary button */
  secondaryLabel?: string;
  /** Disable the primary button */
  primaryDisabled?: boolean;
  /** When true, shows skeleton placeholders for buttons */
  loading?: boolean;
  /** When true, disables buttons and shows a spinner on the primary button */
  submitting?: boolean;
  /** Custom action content — overrides variant layout when provided */
  children?: React.ReactNode;
}

/** Default labels per variant */
const VARIANT_LABELS: Record<AkomiDialogActionsProps['variant'], { primary: string; secondary?: string }> = {
  confirm: { primary: 'Confirm' },
  'cancel-confirm': { primary: 'Confirm', secondary: 'Cancel' },
  'delete-cancel': { primary: 'Delete', secondary: 'Cancel' },
  'save-cancel': { primary: 'Save', secondary: 'Cancel' },
  close: { primary: 'Close' },
};

/** Mockup shimmer animation for button skeletons */
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

/** Skeleton matching button dimensions with mockup shimmer */
const skeletonSx = {
  height: 36,
  borderRadius: '4px',
  background: (theme: Theme) =>
    `linear-gradient(90deg, ${theme.palette.grey[600]} 25%, ${theme.palette.grey[500]} 50%, ${theme.palette.grey[600]} 75%)`,
  backgroundSize: '400px 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
} as const;

/**
 * Shared button sx — matches .btn from mockup:
 * font-size: 14px, font-weight: 500, padding: 6px 16px, border-radius: 4px, text-transform: none
 */
const btnBase = {
  fontSize: '14px',
  fontWeight: 500,
  px: '16px',
  py: '6px',
  borderRadius: '4px',
  textTransform: 'none',
  minWidth: '64px',
  lineHeight: 1.75,
} as const;

/**
 * AkomiDialogActions — matches .modal-actions from mockup exactly.
 *
 * Replaces MUI DialogActions to avoid its opinionated padding/justification.
 * Layout: flex, justify-content: flex-end, gap: 8px, padding: 8px 24px 16px.
 */
const AkomiDialogActions: React.FC<AkomiDialogActionsProps> = ({
  variant,
  onPrimary,
  onSecondary,
  primaryLabel,
  secondaryLabel,
  primaryDisabled = false,
  loading = false,
  submitting = false,
  children,
}) => {
  // Custom children bypass variant layout entirely
  if (children) {
    return <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pt: 1, pb: 2 }}>{children}</Box>;
  }

  const labels = VARIANT_LABELS[variant];
  const resolvedPrimaryLabel = primaryLabel ?? labels.primary;
  const resolvedSecondaryLabel = secondaryLabel ?? labels.secondary;
  const isDestructive = variant === 'delete-cancel';
  const hasSecondary = !!resolvedSecondaryLabel;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pt: 1, pb: 2 }}>
      {/* Secondary button — adjacent to primary, both right-aligned */}
      {hasSecondary &&
        (loading ? (
          <Box sx={{ ...skeletonSx, width: 80 }} />
        ) : (
          <Button
            onClick={onSecondary}
            disabled={submitting}
            sx={{
              ...btnBase,
              bgcolor: 'transparent',
              color: (theme) => theme.palette.secondary.main,
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
              },
            }}
          >
            {resolvedSecondaryLabel}
          </Button>
        ))}

      {/* Primary button (right side) */}
      {loading ? (
        <Box sx={{ ...skeletonSx, width: 110 }} />
      ) : isDestructive ? (
        <Button
          onClick={submitting ? undefined : onPrimary}
          variant="contained"
          disabled={primaryDisabled}
          sx={{
            ...btnBase,
            bgcolor: (theme) => theme.palette.error.main,
            color: (theme) => theme.palette.error.contrastText,
            '&:hover': {
              bgcolor: (theme) => lighten(theme.palette.error.main, 0.2),
            },
            '&.Mui-disabled': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.4),
              color: (theme) => alpha(theme.palette.error.contrastText, 0.6),
            },
            ...(submitting && {
              background: (theme: Theme) =>
                `linear-gradient(90deg, ${theme.palette.error.main} 30%, ${lighten(theme.palette.error.main, 0.6)} 50%, ${theme.palette.error.main} 70%)`,
              backgroundSize: '200px 100%',
              animation: `${shimmer} 1.2s ease-in-out infinite`,
              pointerEvents: 'none',
            }),
          }}
        >
          {resolvedPrimaryLabel}
        </Button>
      ) : (
        <Button
          onClick={submitting ? undefined : onPrimary}
          variant="contained"
          disabled={primaryDisabled}
          sx={{
            ...btnBase,
            bgcolor: (theme) => theme.palette.primary.main,
            color: (theme) => theme.palette.primary.contrastText,
            '&:hover': {
              bgcolor: (theme) => lighten(theme.palette.primary.main, 0.2),
            },
            '&.Mui-disabled': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.4),
              color: (theme) => alpha(theme.palette.primary.contrastText, 0.6),
            },
            ...(submitting && {
              background: (theme: Theme) =>
                `linear-gradient(90deg, ${theme.palette.primary.main} 30%, ${lighten(theme.palette.primary.main, 0.6)} 50%, ${theme.palette.primary.main} 70%)`,
              backgroundSize: '200px 100%',
              animation: `${shimmer} 1.2s ease-in-out infinite`,
              pointerEvents: 'none',
            }),
          }}
        >
          {resolvedPrimaryLabel}
        </Button>
      )}
    </Box>
  );
};

export type { AkomiDialogActionsProps };
export { AkomiDialogActions };
export default AkomiDialogActions;
